<template>
    <div :class="$style.content" :data-columns="computedTypeList?.length">
        <div
            v-for="item in computedTypeList"
            :key="item.key"
            :class="$style.card"
            @click="handleSelectType(item)"
        >
            <div :class="$style.introWrap">
                <pt-icon :icon="item.icon" :class="$style.introIcon"></pt-icon>
                <div :class="$style.intro">
                    <h5 :class="$style.title">{{ t(item.name) }}</h5>
                    <p :class="$style.desc">{{ t(item.desc) }}</p>
                </div>
            </div>
            <pt-icon icon="pt-icon-add1" :class="$style.addIcon"></pt-icon>
        </div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'ExperienceTypeSelect'
};
</script>

<script lang="ts" setup>
import { computed } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import useEventBus from '@assets/plugins/eventBus';
import type { TypeItem } from '@experience/static/config/experience.type';
import { experienceTypeList } from '@experience/static/config/experience.config';
import { verifyPtxPackage } from '@models/package/package';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { usePermission } from '@models/profile/useProfilePermission';
import usePlanLimit from '@master/hooks/service/usePlanLimit/usePlanLimit';
import analyticsService from '@assets/services/analytics.service';
import { useRoleDialog } from '@master/hooks/service/useShowAuthDialog';

// interfaces

// props

// emits
const emit = defineEmits(['select']);

// state
const { t } = useI18n();
const { eventBus } = useEventBus();
const { checkAuth, showUpGradeDialog } = usePlanLimit();
const { hasExperienceAdvancePermission } = usePermission();
const { profileInfo } = useProfileInfo();

// computed
const computedTypeList = computed(() => {
    return cloneDeep(experienceTypeList).filter(
        item => item.key !== 'ADVANCED' || hasExperienceAdvancePermission.value
    );
});

const hasABTestAuth = computed(() => {
    return checkAuth({
        limitType: 'useCampaignAbTest',
        currentUsage: null,
        productType: 'ptx',
        editType: 'EDIT',
        isNpPackage: profileInfo.value?.isNpPackage as boolean
    })?.effective;
});

function handleSelectType(item: TypeItem) {
    const { key } = item;
    if (['ABTEST', 'REDIRECT'].includes(key)) {
        verifyPtxPackage(
            'useCampaignAbTest',
            () => {
                if (!useRoleDialog()) return;

                emit('select', item);
                analyticsService
                    .usePTX('cp_add_select_type', {
                        type: item.trackKey
                    })
                    .track();
            },
            (res: any) => {
                showUpGradeDialog({
                    limitType: key === 'ABTEST' ? 'useCampaignAbTest' : 'useCampaignRedirect',
                    privilege: res.privilege,
                    product: 'ptx'
                });
            },
            !!profileInfo.value?.isNpPackage
        );
    } else {
        if (!useRoleDialog()) return;
        emit('select', item);
    }
}

function handleUpgrade() {
    eventBus.$emit('upgrade', {
        type: 'useCampaignAbTest'
    });
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.content {
    width: 100%;
    margin: 0 auto;
    padding: 0 46px;
    display: grid;
    row-gap: 44px;
    align-items: center;
    justify-content: center;
    &[data-columns='4'] {
        grid-template-columns: repeat(4, minmax(270px, 330px));
        column-gap: 44px;
        row-gap: 20px;
    }
    &[data-columns='5'] {
        grid-template-columns: repeat(5, minmax(238px, 330px));
        column-gap: 16px;
        row-gap: 16px;

        .card .introWrap .title {
            font-size: 14px !important;
        }
    }
    .card {
        padding: 22px 46px 22px 16px;
        border-radius: 8px;
        border: 1px solid $pt-black-30;
        position: relative;
        cursor: pointer;
        height: 100%;
        // min-height: 126px;
        &:hover {
            box-shadow: 0 2px 4px 0 rgba(67, 77, 91, 0.22);
        }
        .introWrap {
            display: flex;
            .introIcon {
                flex: 0 0 40px;
                width: 40px;
                height: 40px;
                margin-right: 8px;
            }
            .title {
                font-size: 16px;
                color: $pt-black-900;
                font-weight: 500;
                line-height: 28px;
            }
            .desc {
                font-size: 12px;
                color: $pt-black-90;
            }
        }
        .addIcon {
            width: 24px;
            height: 24px;
            fill: $pt-black-90;
            position: absolute;
            right: 12px;
            top: 30px;
        }
    }
}

@media screen and (max-width: 1279px) {
    .content {
        &[data-columns='4'] {
            max-width: 800px;
            grid-template-columns: repeat(auto-fill, minmax(270px, 330px));
        }
        &[data-columns='5'] {
            grid-template-columns: repeat(auto-fill, minmax(238px, 330px));
        }
    }
}
</style>
